/**
 * Enterprise Lesson Store — tracks active lesson, step progress, completion.
 * Persists progress to localStorage for resume support.
 * Syncs step-level progress to server for login-v2sims dashboard.
 */
import { createStore } from 'zustand/vanilla';
import type { EnterpriseStepProgress } from '@graphicsim/shared-types';

const STORAGE_KEY = 'graphicsim:enterprise:progress';

export interface EnterpriseState {
  activeLessonId: string | null;
  currentSectionIndex: number;
  currentStepIndex: number;
  completedStepIds: string[];
  totalSteps: number;
  isComplete: boolean;
  isActive: boolean;
  // Actions
  startLesson: (lessonId: string, totalSteps: number) => void;
  completeStep: (stepId: string) => void;
  advanceStep: (sectionIndex: number, stepIndex: number) => void;
  completeLesson: () => void;
  exitLesson: () => void;
}

function loadProgress(): Record<string, EnterpriseStepProgress> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveProgress(lessonId: string, progress: EnterpriseStepProgress): void {
  try {
    const all = loadProgress();
    all[lessonId] = progress;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {}
}

export function getStoredProgress(lessonId: string): EnterpriseStepProgress | null {
  const all = loadProgress();
  return all[lessonId] ?? null;
}

export function getAllProgress(): Record<string, EnterpriseStepProgress> {
  return loadProgress();
}

export function clearAllProgress(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
  // Also clear server-side progress
  fetch('/api/v1/enterprise/progress', {
    method: 'DELETE',
    credentials: 'include',
  }).catch(() => {});
}

export function clearLessonProgress(lessonId: string): void {
  try {
    const all = loadProgress();
    delete all[lessonId];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {}
  // Also clear server-side progress for this lesson
  fetch(`/api/v1/enterprise/progress?lessonId=${encodeURIComponent(lessonId)}`, {
    method: 'DELETE',
    credentials: 'include',
  }).catch(() => {});
}

/**
 * Sync progress to the server (enterprise_progress table).
 * Fire-and-forget — localStorage is the primary source for in-app UI,
 * but the server write ensures the login-v2sims dashboard sees it.
 */
function syncProgressToServer(lessonId: string, completedSteps: number, totalSteps: number, isComplete: boolean): void {
  fetch('/api/v1/enterprise/progress', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ lessonId, completedSteps, totalSteps, isComplete }),
  }).catch((err) => {
    console.warn('Failed to sync enterprise progress to server:', err);
  });
}

export const enterpriseStore = createStore<EnterpriseState>((set, get) => ({
  activeLessonId: null,
  currentSectionIndex: 0,
  currentStepIndex: 0,
  completedStepIds: [],
  totalSteps: 0,
  isComplete: false,
  isActive: false,

  startLesson: (lessonId, totalSteps) => {
    const existing = getStoredProgress(lessonId);
    if (existing && !existing.isComplete) {
      set({
        activeLessonId: lessonId,
        currentSectionIndex: existing.currentSectionIndex,
        currentStepIndex: existing.currentStepIndex,
        completedStepIds: [...existing.completedStepIds],
        totalSteps,
        isComplete: false,
        isActive: true,
      });
    } else {
      set({
        activeLessonId: lessonId,
        currentSectionIndex: 0,
        currentStepIndex: 0,
        completedStepIds: [],
        totalSteps,
        isComplete: false,
        isActive: true,
      });
      saveProgress(lessonId, {
        lessonId,
        currentSectionIndex: 0,
        currentStepIndex: 0,
        completedStepIds: [],
        isComplete: false,
        startedAt: new Date().toISOString(),
      });
    }
  },

  completeStep: (stepId) => {
    const state = get();
    if (!state.activeLessonId) return;
    const completed = [...state.completedStepIds, stepId];
    set({ completedStepIds: completed });
    saveProgress(state.activeLessonId, {
      lessonId: state.activeLessonId,
      currentSectionIndex: state.currentSectionIndex,
      currentStepIndex: state.currentStepIndex,
      completedStepIds: completed,
      isComplete: false,
      startedAt: getStoredProgress(state.activeLessonId)?.startedAt ?? new Date().toISOString(),
    });
    // Sync step progress to server
    syncProgressToServer(state.activeLessonId, completed.length, state.totalSteps, false);
  },

  advanceStep: (sectionIndex, stepIndex) => {
    const state = get();
    if (!state.activeLessonId) return;
    set({ currentSectionIndex: sectionIndex, currentStepIndex: stepIndex });
    saveProgress(state.activeLessonId, {
      lessonId: state.activeLessonId,
      currentSectionIndex: sectionIndex,
      currentStepIndex: stepIndex,
      completedStepIds: state.completedStepIds,
      isComplete: false,
      startedAt: getStoredProgress(state.activeLessonId)?.startedAt ?? new Date().toISOString(),
    });
  },

  completeLesson: () => {
    const state = get();
    if (!state.activeLessonId) return;
    const lessonId = state.activeLessonId;
    set({ isComplete: true, isActive: false });
    saveProgress(lessonId, {
      lessonId,
      currentSectionIndex: state.currentSectionIndex,
      currentStepIndex: state.currentStepIndex,
      completedStepIds: state.completedStepIds,
      isComplete: true,
      startedAt: getStoredProgress(lessonId)?.startedAt ?? new Date().toISOString(),
      completedAt: new Date().toISOString(),
    });
    // Sync completion to server
    syncProgressToServer(lessonId, state.completedStepIds.length, state.totalSteps, true);
  },

  exitLesson: () => {
    set({
      activeLessonId: null,
      currentSectionIndex: 0,
      currentStepIndex: 0,
      completedStepIds: [],
      totalSteps: 0,
      isComplete: false,
      isActive: false,
    });
  },
}));
